/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {
  Component,
  Input,
  OnInit,
  Output,
  ChangeDetectionStrategy
} from '@angular/core';
import {PartNumberChangesDetailStickyHeaderConfigurationModel} from '../services/part-number-changes-detail-configuration/models/part-number-changes-detail-sticky-header-configuration.model';
import {Subject} from 'rxjs';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import moment from 'moment';
import {
    Content,
    TDocumentDefinitions
} from 'pdfmake/interfaces';
import {PartNumberChangesDetailExportPdfMapper} from './part-number-changes-detail-export-pdf/part-number-changes-detail-export-pdf.mapper';
import {PartNumberChangesPdfCredentialsModel} from './part-number-changes-detail-export-pdf/models/part-number-changes-pdf-credentials.model';
import {PrettyPrintReferencePipe} from '../../shared/pipes/pretty-print-reference/pretty-print-reference.pipe';
import {PartNumberChangesPdfOldPncModel} from './part-number-changes-detail-export-pdf/models/part-number-changes-pdf-old-pnc.model';
import {PartNumberChangesPdfSolutionModel} from './part-number-changes-detail-export-pdf/models/part-number-changes-pdf-solution.model';
import {PartNumberChangeSolveMaterialDetailModel} from '../../api/ms-partnumberchanges/contracts/new-material/model/part-number-change-solve-material-detail.model';
import {UndefinedValuePipe} from '../../shared/pipes/undefined-value/undefined-value.pipe';
import genericPdfConfiguration from '../../shared/pdf/configuration/generic-pdf-configuration';
import {PrettyPrintMaterialNumberPipe} from '../../shared/pipes/pretty-print-material-number/pretty-print-material-number.pipe';
import {UserBaseContract} from '../../bundles/api/contracts/user-base.contract';
import {AvailableActions} from '../../api/ms-partnumberchanges/contracts/available-actions.enum';
import {PncTypeEnum} from '../models/pnc-type-enum/pnc-type.enum';
import {SwitchButtonConfiguration} from '../../shared/switch-button/configuration/switch-button.configuration';
import {SwitchButtonModel} from '../../shared/switch-button/models/switch-button.model';
import {PartNumberChangeAdditionalFullLotNumberDetailModel} from '../models/additional-full-lot-number-detail/part-number-change-additional-full-lot-number-detail.model';
import {
    footerPdfConfiguration,
    toDataURL
} from '../../shared/pdf/utils/generic-pdf-template-functions';

pdfMake.vfs = pdfFonts.vfs;

@Component({
    selector: 'app-part-number-changes-detail-sticky-header',
    templateUrl: './part-number-changes-detail-sticky-header.component.html',
    styleUrls: ['./part-number-changes-detail-sticky-header.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class PartNumberChangesDetailStickyHeaderComponent implements OnInit {
    @Input() configuration: PartNumberChangesDetailStickyHeaderConfigurationModel;
    @Output() done: Subject<boolean> = new Subject<boolean>();
    readonly EXPORT_BUTTON_TEXT = 'Export PDF';
    public readonly PNC_FLYING_MENU_TOOLTIP = 'View who marked/unmarked as done and when';
    public readonly PNC_MARKED_BY_LABEL = 'Last marked/unmarked by:';
    public readonly PNC_MARKED_ON_LABEL = 'Marked/unmarked on:';
    pncSwitchButtonConfiguration: SwitchButtonConfiguration = {
        spaceInBetween: 28,
        labelOnRight: false,
        label: 'Mark as done',
        tooltipText: 'Mark as done after closing all your tasks'
    };
    switchButtonModel: SwitchButtonModel;

    constructor(private prettyPrintReferencePipe: PrettyPrintReferencePipe,
                private undefinedValuePipe: UndefinedValuePipe,
                private prettyPrintMaterialNumberPipe: PrettyPrintMaterialNumberPipe) {
    }

    ngOnInit(): void {
        this.switchButtonModel = {
            checked: this.configuration?.done,
            disabled: false
        };
    }

    toggleDone(checked: boolean): void {
        this.done.next(checked);
    }

    genericPdfConfiguration(): TDocumentDefinitions {
        return {
            ...genericPdfConfiguration,
            styles: {
                label: {
                    fontSize: 11,
                    italics: true,
                    color: '#8f8f8f',
                    margin: [0, 8, 12, 4]
                },
                value: {
                    fontSize: 11,
                    margin: [0, 8, 0, 4]
                }
            }
        };
    }

    headerPdfConfiguration(pncType: string): Content {
        return [
            {
                columns: [
                    {
                        text: pncType?.toUpperCase(),
                        width: '50%',
                        height: 20,
                        margin: [35, 35, 35, 0],
                        bold: true
                    }
                ]
            },
            {
                canvas: [
                    {type: 'line', x1: 35, y1: 0, x2: 304, y2: 0}
                ]
            }
        ];
    }

    credentialsInformationTable(configuration: PartNumberChangesPdfCredentialsModel): Content {
        const createdOn = this.transformDate(configuration.createdOn)
                .toString();
        return [
            {
                columns: [
                    {
                        text: 'PNC NO.:',
                        width: '25%',
                        style: 'label'
                    },
                    {
                        text: configuration.pncNumber,
                        width: 139,
                        style: 'value'
                    },
                    {
                        text: 'Created on:',
                        width: '25%',
                        style: 'label'
                    },
                    {
                        text: createdOn,
                        width: 'auto',
                        style: 'value'
                    }
                ]
            },
            {
                canvas: [{type: 'line', x1: 0, y1: 0, x2: 525, y2: 0, lineWidth: 0.5, lineColor: '#d8d8d8'}]
            },
            {
                columns: [
                    {
                        text: 'Partner:',
                        width: '25%',
                        style: 'label'
                    },
                    {
                        text: this.prettyPrintReferencePipe.transform(configuration.partner),
                        width: 'auto',
                        style: 'value'
                    }
                ]
            },
            {
                canvas: [{type: 'line', x1: 0, y1: 0, x2: 525, y2: 0, lineWidth: 0.5, lineColor: '#d8d8d8'}]
            },
            {
                columns: [
                    {
                        text: 'Country/region:',
                        width: '25%',
                        style: 'label'
                    },
                    {
                        text: configuration.country,
                        width: 'auto',
                        style: 'value'
                    }
                ]
            },
            {
                canvas: [{type: 'line', x1: 0, y1: 0, x2: 525, y2: 0, lineWidth: 0.5, lineColor: '#d8d8d8'}]
            },
            {
                columns: [
                    {
                        text: 'Ship to party:',
                        width: '25%',
                        style: 'label'
                    },
                    {
                        text: this.prettyPrintReferencePipe.transform(configuration.shipToParty),
                        width: 'auto',
                        style: 'value'
                    }
                ]
            },
            {
                canvas: [{type: 'line', x1: 0, y1: 0, x2: 525, y2: 0, lineWidth: 0.5, lineColor: '#d8d8d8'}]
            },
            {
                columns: [
                    {
                        text: 'Supplying Packing Plant:',
                        width: '25%',
                        style: 'label'
                    },
                    {
                        text: this.prettyPrintReferencePipe.transform(configuration.supplyingPackingPlant),
                        width: 'auto',
                        style: 'value'
                    }
                ]
            },
            {
                canvas: [{type: 'line', x1: 0, y1: 0, x2: 525, y2: 0, lineWidth: 0.5, lineColor: '#d8d8d8'}]
            },
            {
                canvas: [
                    {type: 'line', x1: 0, y1: 0, x2: 525, y2: 0, lineWidth: 0.5}
                ],
                margin: [0, 16, 0, 12]
            }
        ];
    }

    credentialsOldPartNumberChangeTable(configuration: PartNumberChangesPdfOldPncModel): Content {
        const lotNumber = this.fullLotNumbersFormatter([
            {fullLotNumber: configuration.fullLotNumber, quantity: configuration.quantity},
            ...configuration.additionalFullLotNumbers
        ]);
        const spoNumber = this.spoNumbersFormatter(configuration.spoNumber, configuration.additionalSpoNumbers);

        const config: Content = [
            {
                columns: [
                    {
                        text: 'Old Part NO.:',
                        width: '25%',
                        style: 'label'
                    },
                    {
                        text: this.prettyPrintMaterialNumberPipe.transform(configuration.oldPartNumber),
                        width: 'auto',
                        style: 'value'
                    }
                ]
            },
            {
                columns: [
                    {
                        text: 'Quantity:',
                        width: '25%',
                        style: 'label'
                    },
                    {
                        text: this.undefinedValuePipe.transform(configuration?.quantity?.toString()),
                        width: 'auto',
                        style: 'value'
                    }
                ]
            },
            {
                canvas: [{type: 'line', x1: 0, y1: 0, x2: 525, y2: 0, lineWidth: 0.5, lineColor: '#d8d8d8'}]
            },
            {
                columns: [
                    {
                        text: 'Sales Variant:',
                        width: '25%',
                        style: 'label'
                    },
                    {
                        text: this.undefinedValuePipe.transform(configuration.salesVariant),
                        width: 'auto',
                        style: 'value'
                    }
                ]
            },
            {
                canvas: [{type: 'line', x1: 0, y1: 0, x2: 525, y2: 0, lineWidth: 0.5, lineColor: '#d8d8d8'}]
            },
            {
                columns: [
                    {
                        text: 'Model Code:',
                        width: '25%',
                        style: 'label'
                    },
                    {
                        text: this.undefinedValuePipe.transform(configuration.modelCode),
                        width: 139,
                        style: 'value'
                    },
                    {
                        text: 'Color:',
                        width: '25%',
                        style: 'label'
                    },
                    {
                        text: this.undefinedValuePipe.transform(configuration.color),
                        width: 'auto',
                        style: 'value'
                    }
                ]
            },
            {
                canvas: [{type: 'line', x1: 0, y1: 0, x2: 525, y2: 0, lineWidth: 0.5, lineColor: '#d8d8d8'}]
            },
            {
                columns: [
                    {
                        text: 'Full Lot NO.(s) / PO NO.:',
                        width: '25%',
                        style: 'label'
                    },
                    {
                        text: this.undefinedValuePipe.transform(lotNumber),
                        width: 'auto',
                        style: 'value'
                    }
                ]
            },
            {
                canvas: [{type: 'line', x1: 0, y1: 0, x2: 525, y2: 0, lineWidth: 0.5, lineColor: '#d8d8d8'}]
            },
            {
                columns: [
                    {
                        text: 'Problem Description:',
                        width: '25%',
                        style: 'label'
                    },
                    {
                        text: this.undefinedValuePipe.transform(configuration.problemDescription),
                        width: '380',
                        style: 'value'
                    }
                ]
            },
            {
                canvas: [{type: 'line', x1: 0, y1: 0, x2: 525, y2: 0, lineWidth: 0.5, lineColor: '#d8d8d8'}]
            },
            {
                canvas: [
                    {type: 'line', x1: 0, y1: 0, x2: 525, y2: 0, lineWidth: 0.5}
                ],
                margin: [0, 16, 0, 12]
            }
        ];

        if (configuration.pncType === PncTypeEnum.PLANNER || configuration.pncType === PncTypeEnum.AI_CHANGE) {
            const spoColumn: Content = [
                {
                    columns: [
                        {
                            text: 'SPO NO.:',
                            width: '25%',
                            style: 'label'
                        },
                        {
                            text: this.undefinedValuePipe.transform(spoNumber),
                            width: 'auto',
                            style: 'value'
                        }
                    ]
                },
                {
                    canvas: [{type: 'line', x1: 0, y1: 0, x2: 525, y2: 0, lineWidth: 0.5, lineColor: '#d8d8d8'}]
                }
            ];

            config.splice(-1, 0, spoColumn);
        }

        return config;
    }

    pncSolutionTable(configuration: PartNumberChangesPdfSolutionModel, numberAi: boolean): Content {
        let result: Content = [
            {
                columns: [
                    {
                        text: numberAi ? 'New part number AI:' : 'New part NO.:',
                        width: '25%',
                        style: 'label'
                    },
                    {
                        text: this.prettyPrintMaterialNumberPipe.transform(configuration.newMaterial.material),
                        width: 'auto',
                        style: 'value'
                    }
                ]
            },
            {
                columns: [
                    {
                        text: 'Quantity:',
                        width: '25%',
                        style: 'label'
                    },
                    {
                        text: this.undefinedValuePipe.transform(configuration.newMaterial?.quantity?.toString()),
                        width: 'auto',
                        style: 'value'
                    }
                ]
            },
            {
                canvas: [{type: 'line', x1: 0, y1: 0, x2: 525, y2: 0, lineWidth: 0.5, lineColor: '#d8d8d8'}]
            }
        ];

        if (!numberAi && configuration.solutionSupplyingPackingPlant) {
            let spp = {
                columns: [
                    {
                        text: 'Supplying Packing Plant:',
                        width: '25%',
                        style: 'label'
                    },
                    {
                        text: this.prettyPrintReferencePipe.transform(configuration.solutionSupplyingPackingPlant),
                        width: 'auto',
                        style: 'value'
                    }
                ]
            };
            result.unshift(spp);
        }

        let pncSolutionSectionHeader: Content = [
            {
                columns: [
                    {
                        text: "PNC SOLUTION",
                        width: '50%',
                        height: 20,
                        margin: [0, 25, 35, 0],
                        bold: true
                    }
                ]
            },
            {
                canvas: [
                    {type: 'line', x1: 0, y1: 0, x2: 274, y2: 0}
                ]
            }
        ];
        result.unshift(pncSolutionSectionHeader);

        return result;
    }

    mapAdditionalMaterials(additionalMaterials: PartNumberChangeSolveMaterialDetailModel[]): Content {
        const additionalMaterial: any = [];
        additionalMaterials.forEach((value, index) => {
            const number = index + 1;
            const label = 'Additional Part NO. ' + number + ':';
            const result = [
                {
                    columns: [
                        {
                            text: label,
                            width: '25%',
                            style: 'label'
                        },
                        {
                            text: this.prettyPrintMaterialNumberPipe.transform(value.material),
                            width: 'auto',
                            style: 'value'
                        }
                    ]
                },
                {
                    columns: [
                        {
                            text: 'Quantity:',
                            width: '25%',
                            style: 'label'
                        },
                        {
                            text: this.undefinedValuePipe.transform(value?.quantity?.toString()),
                            width: 'auto',
                            style: 'value'
                        }
                    ]
                },
                {
                    canvas: [{type: 'line', x1: 0, y1: 0, x2: 525, y2: 0, lineWidth: 0.5, lineColor: '#d8d8d8'}]
                }
            ];
            additionalMaterial.push(result);
        });
        return additionalMaterial;
    }

    mapAdditionalMaterialsInformation(additionalMaterialsInformation: PartNumberChangesPdfSolutionModel): Content {
        return [
            {
                columns: [
                    {
                        text: 'Additional information:',
                        width: '25%',
                        style: 'label'
                    },
                    {
                        text: this.undefinedValuePipe.transform(additionalMaterialsInformation.additionalInformation),
                        width: '380',
                        style: 'value'
                    }
                ]
            },
            {
                canvas: [{type: 'line', x1: 0, y1: 0, x2: 525, y2: 0, lineWidth: 0.5, lineColor: '#d8d8d8'}]
            },
            {
                columns: [
                    {
                        text: 'Solved by:',
                        width: '25%',
                        style: 'label'
                    },
                    {
                        text: additionalMaterialsInformation.solvedBy.email,
                        width: 'auto',
                        style: 'value'
                    }
                ]
            },
            {
                canvas: [{type: 'line', x1: 0, y1: 0, x2: 525, y2: 0, lineWidth: 0.5, lineColor: '#d8d8d8'}]
            },
            {
                columns: [
                    {
                        text: 'Solved date:',
                        width: '25%',
                        style: 'label'
                    },
                    {
                        text: this.transformDate(additionalMaterialsInformation.solvedOn),
                        width: 'auto',
                        style: 'value'
                    }
                ]
            },
            {
                canvas: [{type: 'line', x1: 0, y1: 0, x2: 525, y2: 0, lineWidth: 0.5, lineColor: '#d8d8d8'}]
            }
        ];
    }

    mapSolutionEEInformation(solutionEEInformation: PartNumberChangesPdfSolutionModel): Content {
        return [
            {
                canvas: [
                    {type: 'line', x1: 0, y1: 0, x2: 525, y2: 0, lineWidth: 0.5}
                ],
                margin: [0, 16, 0, 12]
            },
            {
                columns: [
                    {
                        text: 'I-level:',
                        width: '25%',
                        style: 'label'
                    },
                    {
                        text: this.undefinedValuePipe.transform(solutionEEInformation.iLevel),
                        width: 'auto',
                        style: 'value'
                    }
                ]
            },
            {
                canvas: [{type: 'line', x1: 0, y1: 0, x2: 525, y2: 0, lineWidth: 0.5, lineColor: '#d8d8d8'}]
            },
            {
                columns: [
                    {
                        text: 'Additional information:',
                        width: '25%',
                        style: 'label'
                    },
                    {
                        text: this.undefinedValuePipe.transform(solutionEEInformation.eeAdditionalInformation),
                        width: '380',
                        style: 'value'
                    }
                ]
            },
            {
                canvas: [{type: 'line', x1: 0, y1: 0, x2: 525, y2: 0, lineWidth: 0.5, lineColor: '#d8d8d8'}]
            },
            {
                columns: [
                    {
                        text: 'Validated by:',
                        width: '25%',
                        style: 'label'
                    },
                    {
                        text: this.undefinedValuePipe.transform(solutionEEInformation.validatedBy.email),
                        width: 'auto',
                        style: 'value'
                    }
                ]
            }
        ];
    }

    generatePdf(footerImage: string): TDocumentDefinitions {
        const content: Content[] = [];
        const headerInformation = this.configuration?.data?.type?.name;
        const credentialsInformation = PartNumberChangesDetailExportPdfMapper
                .fromPncDetailToCredentialsInformation(this.configuration.data);
        const oldPartNumberChangeInformation = PartNumberChangesDetailExportPdfMapper
                .fromPncDetailToOldPartNumberChangeInformation(this.configuration.data);
        const pncSolutionInformation = PartNumberChangesDetailExportPdfMapper
                .fromPncDetailToPncSolutionInformation(this.configuration.data);

        content.push(this.credentialsInformationTable(credentialsInformation));
        content.push(this.credentialsOldPartNumberChangeTable(oldPartNumberChangeInformation));
        if (pncSolutionInformation.newMaterial != null) {
            content.push(this.pncSolutionTable(pncSolutionInformation, this.configuration?.data?.type?.code === PncTypeEnum.AI_CHANGE));
            if (pncSolutionInformation.additionalMaterials != null) {
                content.push(this.mapAdditionalMaterials(pncSolutionInformation.additionalMaterials));
            }
            content.push(this.mapAdditionalMaterialsInformation(pncSolutionInformation));
        }
        if (pncSolutionInformation.requiresFlashing) {
            content.push(this.mapSolutionEEInformation(pncSolutionInformation));
        }

        return {
            ...this.genericPdfConfiguration(),
            content,
            header: (): Content => this.headerPdfConfiguration(headerInformation),
            footer: (currentPage: number, pageCount: number): Content => footerPdfConfiguration(currentPage, pageCount, footerImage)
        };
    }

    canMarkAsDone(): boolean {
        return this.configuration?.data?.availableActions?.includes(AvailableActions.DONE)
                || this.configuration?.data?.availableActions?.includes(AvailableActions.UNDONE);
    }

    async exportPdf(): Promise<void> {
        await new Promise(async (resolve) => {
            const pdfName = `PNC_${this.configuration?.data?.number}_solution`;
            const base64image = await toDataURL('/assets/ORCAN-logo.png')
                    .then((dataUrl: any) => dataUrl);

            pdfMake.createPdf(this.generatePdf(base64image))
                    .download(pdfName);
            resolve(null);
        });
    }

    /**
     * Returns the if the pnc was yet marked
     */
    get pncIsMarked(): boolean {
        return this.configuration.data?.doneBy != null && this.configuration.data?.doneOn != null;
    }

    /**
     * Returns the user that sent the bundle
     */
    get pncDoneBy(): UserBaseContract {
        return this.configuration.data?.doneBy;
    }

    /**
     * Returns the date when the pnc was marked as done/undone
     */
    get pncDoneOn(): string {
        return moment(this.configuration.data?.doneOn)
                .format('DD-MM-YYYY HH:mm:ss');
    }

    private transformDate(date: number): string {
        if (date == null) {
            return '-';
        }
        return moment(date)
                .format('DD-MMM-YYYY');
    }

    private fullLotNumbersFormatter(fullLotNumbers: PartNumberChangeAdditionalFullLotNumberDetailModel[]): string {
        return fullLotNumbers?.map((elem) => elem.quantity ? `${elem.fullLotNumber} (${elem.quantity})` : elem.fullLotNumber)
                .join(', ') ?? '';
    }

    private spoNumbersFormatter(spoNumber: string, additionalSpoNumbers?: string[]): string {
        let value = '';

        if (spoNumber != null) {
            value += spoNumber;

            if (additionalSpoNumbers != null) {
                additionalSpoNumbers.forEach((item) => {
                    value = `${value}, ${item}`;
                });
            }
        }

        return value;
    }
}
